import { memo, type CSSProperties } from 'react';
import { cx } from '@linaria/atomic';
import { motion, AnimatePresence } from 'motion/react';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { CheckIconSmall } from '../../../icon';
import { Heading } from '../../heading';
import { Text } from '../../text';
import { useTooltip } from '../../tooltip';
import { Status, useStepperContext } from '../provider/stepper.provider';
import * as styles from './stepper-horizontal.styles';

export type StepperHorizontalProps = {
  numSteps: number;
  stepTitleText?: {
    [key: number]: string;
  };
};

export const StepperHorizontal = ({ numSteps, stepTitleText }: StepperHorizontalProps) => {
  const { stepStatus, handleStepperCard } = useStepperContext();
  const stepArray: number[] = [];
  for (let i = 1; i <= numSteps; i++) {
    stepArray.push(i);
  }

  return (
    <StepperHorizontalRaw
      steps={stepArray.map((step) => ({ title: stepTitleText?.[step], status: stepStatus[step]! }))}
      onStepClick={handleStepperCard}
    />
  );
};

const stepStyles = {
  active: {
    backgroundColor: theme.colors.primary50,
    border: `2px solid ${theme.colors.primary50}`,
    color: theme.colors.white,
  },
  inactive: {
    backgroundColor: theme.colors.white,
    color: theme.colors.neutral40,
    border: `2px solid ${theme.colors.neutral40}`,
  },
  initial: {
    backgroundColor: theme.colors.neutral40,
    color: theme.colors.primary50,
    border: `2px solid ${theme.colors.neutral40}`,
  },
  completed: {
    color: theme.colors.primary50,
    backgroundColor: theme.colors.white,
    border: `2px solid ${theme.colors.primary50}`,
    transition: {
      delay: 0,
      duration: 0.5,
    },
  },
  currActive: {
    backgroundColor: theme.colors.primary50,
    border: `2px solid ${theme.colors.primary50}`,
    color: theme.colors.white,
  },
  errorActive: {
    backgroundColor: theme.colors.status.critical,
    border: `2px solid ${theme.colors.status.critical}`,
    color: theme.colors.white,
  },
  error: {
    backgroundColor: theme.colors.status.critical,
    border: `2px solid ${theme.colors.status.critical}`,
    color: theme.colors.white,
  },
};

const stepLineStyles = {
  active: {
    width: '50%',
    transition: {
      delay: 1,
    },
  },
  currActive: {
    width: '50%',
    transition: {
      delay: 1,
    },
  },
  errorActive: {
    width: '50%',
    transition: {
      delay: 1,
    },
  },
  completed: {
    width: '100%',
    transition: {
      delay: 0,
    },
  },
};

type Step = {
  title: string | undefined;
  status: Status;
  stepDescription?: string;
};

type StepperHorizontalRawProps = {
  steps: Step[];
  onStepClick: (step: number) => void;
  showStepCount?: boolean;
  className?: string;
  interactive?: boolean;
};

export const StepperHorizontalRaw = ({
  className,
  steps,
  onStepClick,
  showStepCount = true,
  interactive = true,
}: StepperHorizontalRawProps) => {
  if (steps.length === 0) return null;

  return (
    <div className={cx('stepper-horizontal', className, styles.horizontalContainer)}>
      {steps.map((step, i) => (
        <HorizontalStepperItem
          key={i}
          step={step}
          currentStepIndex={i}
          onStepClick={onStepClick}
          totalStepsCount={steps.length}
          showStepCount={showStepCount}
          interactive={interactive}
        />
      ))}
    </div>
  );
};

type HorizontalStepperItemProps = {
  step: Step;
  onStepClick: (step: number) => void;
  showStepCount?: boolean;
  currentStepIndex: number;
  totalStepsCount: number;
  interactive?: boolean;
};

const HorizontalStepperItem = memo(
  ({
    currentStepIndex,
    onStepClick,
    step,
    totalStepsCount,
    showStepCount,
    interactive = true,
  }: HorizontalStepperItemProps) => {
    const isMobile = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
    const { tooltipProps, triggerProps, Tooltip } = useTooltip();
    const handleStepClick = () => onStepClick(currentStepIndex);
    const stepNumber = currentStepIndex + 1;

    return (
      <div
        className={cx('stepper-horizontal-step', styles.horizontalStep)}
        style={{ '--stepper-horizontal-step-width': `${100 / totalStepsCount}%` } as CSSProperties}
      >
        <div className={cx('step-box', styles.stepBox(step.status))}>
          {step.stepDescription && <Tooltip {...tooltipProps}>{step.stepDescription}</Tooltip>}
          <motion.div
            animate={step.status}
            variants={stepStyles}
            transition={{ ease: 'easeOut', duration: 0.5 }}
            className={cx('step-num-horizontal', styles.stepNumHorizontal(step.status, interactive))}
            onClick={interactive ? handleStepClick : undefined}
            {...triggerProps}
          >
            {step.status === 'completed' ? (
              <AnimatePresence>
                <CheckIconSmall />
              </AnimatePresence>
            ) : (
              showStepCount && <Heading level={2}>{stepNumber}</Heading>
            )}
          </motion.div>
          <div className='step-line-horizontal inactive'>
            <motion.div
              animate={step.status}
              variants={stepLineStyles}
              transition={{ ease: 'easeOut', duration: 0.5 }}
              className={cx('step-line-horizontal', styles.stepLineHorizontal(step.status))}
            ></motion.div>
          </div>
        </div>
        {step.title && (
          <Text
            className={cx('step-title-text', styles.stepTitleText(isMobile, step.status, interactive))}
            onClick={interactive ? handleStepClick : undefined}
            data-step-status={step.status}
          >
            {step.title}
          </Text>
        )}
      </div>
    );
  }
);

HorizontalStepperItem.displayName = 'HorizontalStepperItem';
